// КОПИ ПАСПОРТА §IV/§V ИЗ `switch` И ШАБЛОНОВ КОМПОНЕНТОВ — Н40 `3eb19875cbb881df9f5ac353b282baf0`, `s948.d1`.
//
// Пин-список Н27 (cc-173): фразы, которые паспорт печатал литералами внутри компонентов, переехали
// сюда таблицами по образцу `findingBank.js` (B6-2) и `chartCopy.js` (B8-1). Байты печати не менялись —
// приёмка тикета сравнивает каждый `data-field` корпуса до/после.
//
// ⭐ ЗДЕСЬ ТОЛЬКО СЛОВА. Какую строку брать (класс, счёт, состояние) решают компоненты; шаблон получает
// уже вычисленные слоты — номер этапа римской цифрой, число словом, печатное имя слоя. Дом фраз —
// Notion-банк (`s655.d1`), здесь перенос буква в букву.
//
// ⛔ Вне модуля намеренно: заглушка `[TODO-COPY]` (`sectionIVStanding.js`; вторая — `SectionIV.jsx` `theShape` — снята Н41 `a22e929`
// хвост) — ждут фраз Кирилла (пины #2, #7); бровь чарта — уже в `chartCopy.js` (`carrierEyebrow`, s951).

// ---- §IV `the_shape` (пин #1) ----

/**
 * Ветка `count==1` (s504, `s948.d1`): число в слоте формы — один шаг. Четыре формы, у остальных
 * одиннадцати такого слота нет или он не достигает единицы.
 * @type {Record<string, (s: {peak: string, low: string}) => string>}
 */
export const SHAPE_ONE = {
  'monotonic-descent': () => 'the line never turns up. it starts high and drops once — a single step down.',
  'monotonic-ascent': () => 'the line never turns down. it starts low and rises once — a single step up.',
  'peak-then-fall': (s) => `the line rises into stage ${s.peak}, then drops the stage after — a single move down.`,
  'V-shape': (s) => `the line falls to its lowest at stage ${s.low}, then climbs back up — one stage up from the bottom.`,
};

/**
 * Форма по классу геометрии, 15 записей. Слоты: римские номера этапов (`peak`, `low`, `steepDownTo`,
 * `steepUpTo`, `steepDownFrom`, `steepUpFrom`, `peak1`, `peak2`, `low1`, `low2`) и числа словом
 * (`steps`, `drops`, `rises`, `turns`).
 * @type {Record<string, (s: object) => string>}
 */
export const SHAPE = {
  'monotonic-descent': (s) => `the line never turns up. it starts high and drops at every stage — ${s.steps} steps down, one after another.`,
  'monotonic-ascent': (s) => `the line never turns down. it starts low and rises at every stage — ${s.steps} steps up, one after another.`,
  plateau: () => 'the line barely moves. it sits at nearly one level from the first stage to now — no real climb, no real fall.',
  'peak-then-fall': (s) => `the line rises into stage ${s.peak}, then drops every stage after — ${s.drops} moves down in a row.`,
  'V-shape': (s) => `the line falls to its lowest at stage ${s.low}, then climbs back up — ${s.rises} stages up from the bottom.`,
  'late-cliff': (s) => `the line holds level for most of the arc, then drops hard at stage ${s.steepDownTo} — the whole fall comes at the end.`,
  'late-rise': (s) => `the line holds level for most of the arc, then rises hard at stage ${s.steepUpTo} — the whole climb comes at the end.`,
  'early-cliff': (s) => `the line drops hard at the start, from stage ${s.steepDownFrom} to ${s.steepDownTo}, then holds low the rest of the way.`,
  'early-rise': (s) => `the line rises hard at the start, from stage ${s.steepUpFrom} to ${s.steepUpTo}, then holds high the rest of the way.`,
  oscillation: (s) => `the line changes direction over and over — ${s.turns} turns across the arc — never settling into one steady move.`,
  'double-peak': (s) => `the line reaches a high at stage ${s.peak1}, then drops, then reaches a second high at stage ${s.peak2} — two peaks, one dip between them.`,
  'double-valley': (s) => `the line drops to a low at stage ${s.low1}, then lifts, then drops to a second low at stage ${s.low2} — two lows, one rise between them.`,
  'way-back': (s) => `the line falls below where it began, reaches its lowest at stage ${s.low}, then turns back toward the start.`,
  'high-held-collapse': () => 'the line holds high across the arc, then drops sharply at the end — its lowest point is right now.',
  irregular: () => 'the line moves from stage to stage but never into one shape — no steady climb, no single fall.',
};

// ---- §IV `the_move` (пин #3) ----

/** Шаблон хода: голова + хвост по источнику меток (BG1 / BG2 / нет). */
export const MOVE = {
  head: (word, from, to) => `the steepest ${word}, stage ${from} to ${to}`,
  word: { up: 'climb', down: 'drop' },
  bg1: (head, n, labels) => `${head}, came from ${n} ${labels.length === 1 ? 'choice' : 'choices'} you made: ${labels.join(' · ')}.`,
  bg2: (head, one, label) => `${head}, came from ${one} choice you made: ${label}.`,
  none: (head) => `${head}.`,
};

// ---- §IV `intro_paragraph` — шесть веток по раскладу (s304, пин #4) ----

/**
 * Слот `m` — печатные имена слоёв в порядке движка. HTML (`<em>`) — часть строки, как и было.
 * Ключ `default` — все прочие расклады (на деле scatter).
 * @type {Record<string, (m: string[]) => string>}
 */
export const INTRO = {
  pair: (m) => `two layers carried this arc together — <em>${m[0]}</em> and <em>${m[1]}</em>. they move so closely the chart draws them as one red line. which one led is the question the patterns chapter answers.`,
  triplet: (m) => `three layers carried this arc together — <em>${m[0]}</em>, <em>${m[1]}</em> and <em>${m[2]}</em>. which one led is the question the patterns chapter answers.`,
  field: () => 'the layers carried this arc as one field. they move so closely the chart draws them as a single red line. which of them led is the question the patterns chapter answers.',
  singleton: (m) => `one layer carried this arc — <em>${m[0]}</em>. it pulled far enough from the rest that the chart draws it as the red line. what set it apart is the question the patterns chapter answers.`,
  split: (m) => `two layers carried this arc — <em>${m[0]}</em> and <em>${m[1]}</em> — but they pulled apart, not together. the chart follows the stronger of the two as the red line. which of them the arc belongs to is the question the patterns chapter answers.`,
  default: () => 'here is the red line of your life. all seven layers carried it through every stage. the patterns underneath are on the pages ahead.',
};

// ---- §IV `where_it_stands_now` — восемь состояний, регистр §IV (s323.d1/s390.d4, пин #6) ----

/**
 * Числа — словом. Состояния с именованным слоем возвращают ХВОСТ после имени: имя печатается
 * отдельным сегментом (amber), его оборачивает компонент.
 */
export const STANDING = {
  scatter: () => 'nothing holds together — each layer on its own.', // (5)
  twoSidedHighFirst: (high, low) => `${high} layers sit high while ${low} sit at the floor.`, // (7)
  twoSidedLowFirst: (low, high) => `${low} layers sit at the floor while ${high} sit high.`, // (7)
  oneAboveLow: (oth) => ` sits high while ${oth} other layers sit at the floor.`, // (1), хвост после имени
  oneBelowHigh: (oth) => ` sits at the floor while ${oth} other layers sit high.`, // (2), хвост после имени
  allLow: (t) => `all ${t} sit at the floor together.`, // (3)
  allHigh: (t) => `all ${t} sit high together.`, // (4)
  allMid: (t) => `all ${t} sit in the middle together.`, // (6)
  midOne: (side, oth) => ` sits ${side} while ${oth} other layers sit in the middle.`, // (8a/8b), хвост после имени
  midSplit: (rest) => `one layer sits high and one sits at the floor while ${rest} others sit in the middle.`, // (8e)
  midSame: (n, side, rest) => `${n} layers sit ${side} while ${rest} others sit in the middle.`, // (8c/8d)
  side: { up: 'high', down: 'at the floor' },
};

// ---- §V A.3.1 «moving as one» (решение web-Claude, 8б; пин #8) ----

/** Слоты: `n` — число слоёв словом, `layers` — печатный список. Заголовок слоёв не называет. */
export const CELL_A31 = {
  h: 'moving as one.',
  p: (n, layers) => `these ${n} lines on your map have been moving as one: ${layers}. they rise together, they fall together — one thread, not ${n} separate things.`,
};
